import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { waLink } from "@/lib/site";

export const WhatsAppButton = ({
  size = "md",
  variant = "primary",
  label = "Order on WhatsApp",
  testId = "wa-cta",
  className = "",
  msg,
  magnetic = true,
}) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { stiffness: 220, damping: 14, mass: 0.4 };
  const mouseX = useSpring(x, springConfig);
  const mouseY = useSpring(y, springConfig);

  const handleMouseMove = (e) => {
    if (!magnetic || !ref.current) return;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const distanceX = e.clientX - centerX;
    const distanceY = e.clientY - centerY;
    x.set(distanceX * 0.28);
    y.set(distanceY * 0.28);
  };

  const handleMouseLeave = () => {
    if (!magnetic) return;
    x.set(0);
    y.set(0);
  };

  const sizes = {
    sm: "px-4 py-2 text-xs",
    md: "px-6 py-3 text-sm",
    lg: "px-8 py-4 text-base",
  };
  const variants = {
    primary:
      "bg-[#3B2818] text-[#FAF6F0] hover:bg-[#C06A22] hover:text-[#FAF6F0]",
    caramel:
      "bg-[#C06A22] text-[#FAF6F0] hover:bg-[#A65D24]",
    whatsapp:
      "bg-[#25D366] text-white hover:bg-[#1FB455]",
    ghost:
      "bg-transparent text-[#3B2818] border border-[#3B2818]/20 hover:bg-[#3B2818] hover:text-[#FAF6F0]",
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: mouseX, y: mouseY }}
      className="inline-block transform-gpu"
    >
      <a
        data-testid={testId}
        href={waLink(msg)}
        target="_blank"
        rel="noopener noreferrer"
        className={`group inline-flex items-center gap-2 rounded-full font-medium uppercase tracking-[0.14em] transition-[background-color,color,border-color,transform] duration-150 ease-expo-fast active:scale-[0.96] transform-gpu ${sizes[size]} ${variants[variant]} ${className}`}
      >
        <MessageCircle
          className="h-4 w-4 transition-transform duration-250 ease-spring-back group-hover:rotate-[12deg] group-hover:scale-110"
          strokeWidth={1.8}
        />
        <span>{label}</span>
        <span
          aria-hidden
          className="ml-1 inline-block transition-transform duration-200 ease-expo-fast group-hover:translate-x-1"
        >
          →
        </span>
      </a>
    </motion.div>
  );
};
